
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { ImageUpload } from "@/components/ui/image-upload"
import { videoService } from "@/services/video.service"
import { useToast } from "@/hooks/use-toast"
import { Video } from "@/types/api"
import { Loader2 } from "lucide-react"

const formSchema = z.object({
    title: z.string().min(2, "Название должно содержать не менее 2 символов"),
    description: z.string().optional(),
    custom_thumbnail: z.union([z.instanceof(File), z.string(), z.null()]).optional(),
})

interface VideoEditDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    video: Video | null
    onSuccess: () => void
}

export function VideoEditDialog({
    open,
    onOpenChange,
    video,
    onSuccess,
}: VideoEditDialogProps) {
    const { toast } = useToast()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: "",
            description: "",
            custom_thumbnail: null,
        },
    })

    useEffect(() => {
        if (video && open) {
            form.reset({
                title: video.title,
                description: video.description || "",
                custom_thumbnail: video.custom_thumbnail || null,
            })
        }
    }, [video, open, form])

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        if (!video) return

        setIsSubmitting(true)
        try {
            const formData = new FormData()
            formData.append("title", values.title)

            if (values.description) {
                formData.append("description", values.description)
            }

            if (values.custom_thumbnail instanceof File) {
                formData.append("custom_thumbnail", values.custom_thumbnail)
            }

            await videoService.updateVideo(video.id, formData)

            toast({
                title: "Успешно",
                description: "Видео успешно обновлено",
            })
            onSuccess()
            onOpenChange(false)
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось обновить видео",
                variant: "destructive",
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Редактировать видео</DialogTitle>
                    <DialogDescription>
                        Измените информацию о видео и загрузите пользовательскую миниатюру.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="title"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Название</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Описание</FormLabel>
                                    <FormControl>
                                        <Textarea {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="custom_thumbnail"
                            render={({ field: { value, onChange } }) => (
                                <FormItem>
                                    <FormLabel>Пользовательская миниатюра</FormLabel>
                                    <FormControl>
                                        <ImageUpload
                                            value={value}
                                            onChange={onChange}
                                            label="Загрузить миниатюру"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                                Отмена
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Сохранение...
                                    </>
                                ) : (
                                    "Сохранить"
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
